/**
 * handoff.ts — CLIENT-SIDE orchestration for v4-S10 E2EE session handoff.
 *
 * Reuses the G2 code-share pipeline VERBATIM (keystore, per-member X25519 wraps,
 * Ed25519 signatures, XChaCha20-Poly1305 AEAD from `e2ee.ts`) — zero new crypto.
 * The only differences from `code-share.ts` are the transport shape (the
 * `handoff_artifacts` envelope, which carries NO refIndex and NO contract) and
 * the inner payload (a whole session transcript + its metadata, gzipped).
 *
 * ENCRYPTION BOUNDARY (the audit-fix invariant this module enforces):
 *   - INSIDE the ciphertext: sessionId, agent kind, sourceCwd, gitBranch,
 *     headSha, cliVersion, and the full transcript bytes.
 *   - OUTSIDE (the envelope the transport sees): ciphertext, nonce, wrapped
 *     keys, author signature, alg, keyEpoch, bucketed size class, optional
 *     target member uuid, sha256(ciphertext) idempotency. Nothing else.
 *   `shareHandoff` runtime-asserts none of the inner metadata strings appear in
 *   the serialized envelope before it hands anything to the transport.
 *
 * REF/AAD ADAPTATION (documented, honesty-flagged): `e2ee.encryptArtifact` binds
 * its AAD to (roomId, ref, version, keyEpoch, alg) and the author signature to
 * (roomId, ref, version, keyEpoch, sha256(ct), contract). The handoff envelope
 * deliberately has no ref field (a per-artifact random ref could not ride the
 * strict server schema, and the recipient must be able to rebuild the AAD from
 * what it has), so handoffs use ONE fixed, domain-separated ref
 * (`HANDOFF_REF = "aethereum:handoff/v1"`), version 1, and contract "".
 * Per-artifact uniqueness does not depend on the ref: every handoff gets a
 * FRESH random content key, and the author signature binds sha256(ciphertext),
 * so cross-artifact splices fail the AEAD (wrong key) and any relabel fails the
 * signature. Cross-room replay is still killed by roomId in the AAD, and the
 * domain-separated ref keeps a handoff ciphertext from ever verifying as a
 * code-share artifact (whose ref is a blind path index).
 *
 * SIZE: transcripts can exceed the server's 1.5MB ciphertext cap, so the inner
 * JSON payload is gzipped (node:zlib) BEFORE encryption. If the resulting
 * base64 ciphertext still exceeds the cap we refuse politely — the caller
 * should hand off a younger session instead.
 */

import { gzipSync, gunzipSync } from "node:zlib";
import { homedir } from "node:os";
import { e2ee, verify } from "@br9704/aethereum-shared";
import { hasKeystore, loadKeystore, keypairBytes } from "./keystore.js";
import { provisionAndPublish, validRosterEntry, bucketSize, type RosterEntry } from "./code-share.js";
import type { PinnedIdentity } from "./verify-store.js";

const b64 = (bytes: Uint8Array): string => Buffer.from(bytes).toString("base64");
const unb64 = (s: string): Uint8Array => new Uint8Array(Buffer.from(s, "base64"));

/** The one fixed, domain-separated AAD ref for every handoff artifact (see header). */
export const HANDOFF_REF = "aethereum:handoff/v1";

/** Handoffs are single-shot artifacts: no version history, no key rotation (v1). */
const HANDOFF_VERSION = 1;
const HANDOFF_KEY_EPOCH = 1;

/** Server-side cap on the base64 ciphertext (mirrors MAX_CIPHERTEXT_B64 in the
 *  route). Checked client-side AFTER gzip + encryption so we refuse before a
 *  doomed upload. */
export const MAX_HANDOFF_CIPHERTEXT_B64 = 1_500_000;

/** The plaintext INNER payload — everything here travels inside the ciphertext. */
export interface HandoffPayload {
  v: 1;
  sessionId: string;
  agent: "claude" | "codex";
  sourceCwd: string;
  gitBranch?: string;
  headSha?: string;
  cliVersion: string;
  /** Full transcript bytes, base64 (JSON-safe; the JSON is gzipped anyway). */
  transcript: string;
}

/** What the transport stores: the server envelope schema fields, and NOTHING else. */
export interface HandoffEnvelope {
  ciphertext: string;
  nonce: string;
  wrappedKeys: e2ee.WrappedKeyWire[];
  authorSig: string;
  alg: string;
  keyEpoch: number;
  size: number;
  targetMember?: string;
  idempotency: string;
}

/** One list entry (GET /api/sync/handoff — envelope residual only, no ciphertext). */
export interface HandoffListEntry {
  id: string;
  authorMember: string;
  targetMember: string | null;
  alg: string;
  keyEpoch: number;
  size: number;
  createdAt: string;
}

/** One full row (GET /api/sync/handoff/[id]). */
export interface HandoffRow {
  id: string;
  authorMember: string;
  targetMember: string | null;
  ciphertext: string;
  nonce: string;
  wrappedKeys: e2ee.WrappedKeyWire[];
  authorSig: string;
  alg: string;
  keyEpoch: number;
  size: number;
  createdAt?: string;
}

/**
 * The transport the orchestration needs: the three handoff calls plus the SAME
 * public-key directory calls code sharing uses (`publish_key` / `member_keys`
 * on /api/sync/code — member keys are room-scoped, not artifact-kind-scoped).
 * `HostedHandoffClient` and (in tests) an in-memory store both satisfy it.
 */
export interface HandoffTransport {
  storeHandoff(input: HandoffEnvelope): Promise<{ id: string }>;
  listHandoffs(opts?: { limit?: number; before?: string }): Promise<HandoffListEntry[]>;
  fetchHandoff(id: string): Promise<HandoffRow | null>;
  publishPublicKey(input: {
    identityPub: string;
    wrapPub: string;
    wrapPubSig: string;
    alg: string;
  }): Promise<void>;
  memberPublicKeys(): Promise<RosterEntry[]>;
}

/** Thrown when the gzipped + encrypted transcript still exceeds the route cap. */
export class HandoffTooLargeError extends Error {
  constructor(ciphertextB64Length: number) {
    super(
      `This session is too large to hand off even after compression ` +
        `(${Math.round(ciphertextB64Length / 1024)}KB encrypted, cap ${Math.round(MAX_HANDOFF_CIPHERTEXT_B64 / 1024)}KB). ` +
        `Try handing off a younger session with less history, or share the relevant files with \`aethereum share\`.`,
    );
    this.name = "HandoffTooLargeError";
  }
}

/** Serialize + gzip the inner payload (called just before encryption). */
export function packHandoffPayload(payload: HandoffPayload): Uint8Array {
  return new Uint8Array(gzipSync(Buffer.from(JSON.stringify(payload), "utf8")));
}

/** Gunzip + parse the inner payload (called just after decryption). Throws on
 *  any malformed payload — never returns a partial object. */
export function unpackHandoffPayload(bytes: Uint8Array): HandoffPayload {
  const json = gunzipSync(Buffer.from(bytes)).toString("utf8");
  const obj = JSON.parse(json) as Partial<HandoffPayload>;
  if (
    obj.v !== 1 ||
    typeof obj.sessionId !== "string" ||
    !obj.sessionId ||
    (obj.agent !== "claude" && obj.agent !== "codex") ||
    typeof obj.sourceCwd !== "string" ||
    typeof obj.cliVersion !== "string" ||
    typeof obj.transcript !== "string"
  ) {
    throw new Error("handoff payload is malformed (unexpected inner shape)");
  }
  return {
    v: 1,
    sessionId: obj.sessionId,
    agent: obj.agent,
    sourceCwd: obj.sourceCwd,
    gitBranch: typeof obj.gitBranch === "string" ? obj.gitBranch : undefined,
    headSha: typeof obj.headSha === "string" ? obj.headSha : undefined,
    cliVersion: obj.cliVersion,
    transcript: obj.transcript,
  };
}

export interface ShareHandoffResult {
  id: string;
  /** Number of members the content key was wrapped for. */
  recipients: number;
  /** Directory entries dropped (invalid signature, or not the addressed target). */
  dropped: number;
  /** Members excluded because the room requires verified recipients (v6-S1). */
  excluded: string[];
  /** First-contact (unpinned, unverified) members who WERE wrapped. */
  firstContact: string[];
}

/**
 * Encrypt a session handoff and store it (ciphertext-only). The plaintext never
 * leaves this call. When `targetMember` is set, the content key is wrapped ONLY
 * for the target and the author — a non-target member cannot decrypt the
 * artifact even if it somehow obtained the row (the server additionally hides
 * member-addressed rows from everyone else).
 */
export async function shareHandoff(
  transport: HandoffTransport,
  opts: {
    roomId: string;
    member: string;
    payload: HandoffPayload;
    /** Address the handoff to ONE member (room_members uuid). Default: whole room. */
    targetMember?: string;
    /** Pinned identities from `aethereum verify`; divergence is a HARD block. */
    pins?: PinnedIdentity[];
    /** Members covered by an OOB safety-number verification (v6-S1). */
    verified?: string[];
    /**
     * Room policy (client-enforced): when true, only VERIFIED members are
     * wrapped; others land in `ShareHandoffResult.excluded`. An addressed
     * handoff whose target is excluded throws (it must not silently no-op).
     */
    requireVerified?: boolean;
    /**
     * Informed-consent hook for first-contact recipients; return false to
     * abort. Absent => proceed (fail-soft), reported via `firstContact`.
     */
    onFirstContact?: (
      recipients: Array<{ member: string; identityPub: string }>,
    ) => Promise<boolean> | boolean;
    home?: string;
  },
): Promise<ShareHandoffResult> {
  const home = opts.home ?? homedir();
  const plaintext = packHandoffPayload(opts.payload);

  const ks = await provisionAndPublish(transport, opts.roomId, opts.member, home);
  const { identityPriv } = keypairBytes(ks);

  const directory = await transport.memberPublicKeys();
  const valid = directory.filter(validRosterEntry);
  let dropped = directory.length - valid.length;

  // F-1 active-server defence (same as shareCode): a pinned identity that
  // diverged from its pin is a hard block — the server may be substituting keys.
  if (opts.pins && opts.pins.length > 0) {
    const stored = opts.pins.map((p) => ({ member: p.member, identityPub: unb64(p.identityPub) }));
    const presented = valid.map((e) => ({ member: e.member, identityPub: unb64(e.identityPub) }));
    const diverged = verify.detectPinDivergence(stored, presented).filter((d) => d.changed);
    if (diverged.length > 0) {
      throw new Error(
        `refusing to hand off: the identity key for ${diverged.map((d) => d.member).join(", ")} changed since you verified this room. ` +
          "The server may be substituting keys. Re-run `aethereum verify` and compare the safety number out of band first.",
      );
    }
  }

  // Member-addressed: wrap only for the target + the author (so the author can
  // re-open its own offer). Everyone else is excluded from the wraps.
  let recipients = valid;
  if (opts.targetMember) {
    recipients = valid.filter((e) => e.member === opts.targetMember || e.member === opts.member);
    if (!recipients.some((e) => e.member === opts.targetMember)) {
      throw new Error(
        `cannot address this handoff: member ${opts.targetMember} has no validly-signed key in the room directory yet. ` +
          "Ask them to run `aethereum join` / `aethereum init` on their machine first.",
      );
    }
    dropped += valid.length - recipients.length;
  }
  if (recipients.length === 0) {
    throw new Error(
      "cannot hand off: no validly-signed recipients in the directory (nobody has published a signed wrap key yet)",
    );
  }

  // v6-S1 (F-1): classify recipients client-side and apply the room's
  // require-verified policy / first-contact consent, mirroring shareCode.
  const pinList = (opts.pins ?? []).map((p) => ({ member: p.member, identityPub: unb64(p.identityPub) }));
  const verifiedSet = new Set(opts.verified ?? []);
  const otherRecipients = recipients.filter((e) => e.member !== opts.member);
  const firstContactEntries = otherRecipients.filter(
    (e) => verify.classifyRecipient(e.member, pinList, verifiedSet) === "first-contact",
  );
  let excluded: string[] = [];
  if (opts.requireVerified) {
    excluded = otherRecipients
      .filter((e) => verify.classifyRecipient(e.member, pinList, verifiedSet) !== "verified")
      .map((e) => e.member);
    if (opts.targetMember && excluded.includes(opts.targetMember)) {
      throw new Error(
        `refusing to hand off: this room requires verified recipients and ${opts.targetMember} is not verified. ` +
          "Run `aethereum verify` with them (compare the safety number out of band, then `aethereum verify --confirm`) first.",
      );
    }
    recipients = recipients.filter((e) => e.member === opts.member || !excluded.includes(e.member));
    if (!recipients.some((e) => e.member !== opts.member)) {
      throw new Error(
        "refusing to hand off: this room requires verified recipients and none of the other members are verified. " +
          "Run `aethereum verify` with a teammate and try again.",
      );
    }
  } else if (firstContactEntries.length > 0 && opts.onFirstContact) {
    const proceed = await opts.onFirstContact(
      firstContactEntries.map((e) => ({ member: e.member, identityPub: e.identityPub })),
    );
    if (!proceed) {
      throw new Error(
        "handoff declined: unverified first-contact recipients. Run `aethereum verify` to pin and verify them, then hand off again.",
      );
    }
  }
  const firstContact = opts.requireVerified ? [] : firstContactEntries.map((e) => e.member);

  const { roomId } = opts;
  const contentKey = e2ee.generateRoomKey();
  const aad = {
    roomId,
    ref: HANDOFF_REF,
    version: HANDOFF_VERSION,
    keyEpoch: HANDOFF_KEY_EPOCH,
    alg: e2ee.E2EE_ALG,
  };
  const { ciphertext, nonce } = e2ee.encryptArtifact(contentKey, plaintext, aad);
  const ciphertextB64 = b64(ciphertext);
  if (ciphertextB64.length > MAX_HANDOFF_CIPHERTEXT_B64) {
    throw new HandoffTooLargeError(ciphertextB64.length);
  }

  const ctHash = e2ee.ciphertextHash(ciphertext);
  const authorSig = e2ee.signArtifact(identityPriv, {
    roomId,
    ref: HANDOFF_REF,
    version: HANDOFF_VERSION,
    keyEpoch: HANDOFF_KEY_EPOCH,
    ciphertextHash: ctHash,
    contract: "",
  });

  const wrappedKeys: e2ee.WrappedKeyWire[] = recipients.map((entry) => {
    const w = e2ee.wrapRoomKey(contentKey, unb64(entry.wrapPub), entry.member, HANDOFF_KEY_EPOCH, identityPriv);
    return {
      member: w.member,
      epk: b64(w.epk),
      nonce: b64(w.nonce),
      ct: b64(w.ct),
      keyEpoch: w.keyEpoch,
      sig: b64(w.sig),
    };
  });

  const envelope: HandoffEnvelope = {
    ciphertext: ciphertextB64,
    nonce: b64(nonce),
    wrappedKeys,
    authorSig: b64(authorSig),
    alg: e2ee.E2EE_ALG,
    keyEpoch: HANDOFF_KEY_EPOCH,
    size: bucketSize(plaintext.length),
    targetMember: opts.targetMember,
    idempotency: Buffer.from(ctHash).toString("hex"),
  };

  // Encryption-boundary runtime assert: none of the inner metadata may appear
  // anywhere in the serialized envelope (the ciphertext-only guarantee, extended
  // to cwd/branch/sha/session-id per the v4-S10 audit fix).
  const serialized = JSON.stringify(envelope);
  const leakables = [
    opts.payload.sessionId,
    opts.payload.sourceCwd,
    opts.payload.gitBranch,
    opts.payload.headSha,
  ].filter((s): s is string => typeof s === "string" && s.length > 0);
  for (const secret of leakables) {
    if (serialized.includes(secret)) {
      throw new Error("handoff envelope would leak session metadata outside the ciphertext; refusing to send");
    }
  }

  const { id } = await transport.storeHandoff(envelope);
  return { id, recipients: wrappedKeys.length, dropped, excluded, firstContact };
}

export interface FetchHandoffResult {
  payload: HandoffPayload;
  authorMember: string;
  /** True once the author signature verified under the author's directory identity key. */
  authorVerified: boolean;
}

/**
 * Fetch, verify, decrypt and unpack one handoff artifact for THIS member.
 * Returns null when no such artifact exists. Throws (never partial plaintext)
 * on any integrity failure: alg downgrade, missing wrap, author-signature or
 * wrap-signature mismatch, AEAD tag failure — and, when the room was verified
 * (`pins` supplied), on a pinned-identity divergence for the AUTHOR. An
 * UNVERIFIED room (no pins) still round-trips: it is passive-secure (the server
 * stores only ciphertext) but not compromise-resistant until `aethereum verify`.
 */
export async function fetchHandoffArtifact(
  transport: HandoffTransport,
  opts: {
    roomId: string;
    member: string;
    id: string;
    /** Pinned identities from `aethereum verify`; author divergence hard-blocks. */
    pins?: PinnedIdentity[];
    home?: string;
  },
): Promise<FetchHandoffResult | null> {
  const home = opts.home ?? homedir();
  if (!hasKeystore(opts.roomId, home)) {
    throw new Error(
      `no E2EE keystore for room ${opts.roomId}; provision it (aethereum join / init) before resuming a handoff`,
    );
  }
  const ks = loadKeystore(opts.roomId, home);
  const { wrapPriv } = keypairBytes(ks);

  const row = await transport.fetchHandoff(opts.id);
  if (!row) return null;

  // F-3: pin the algorithm to the client constant; refuse any downgrade.
  if (row.alg !== e2ee.E2EE_ALG) {
    throw new Error(`refusing handoff with unsupported/downgraded alg "${row.alg}" (expected "${e2ee.E2EE_ALG}")`);
  }

  // F-A9: a missing wrap is a LOUD signal (not addressed to you, or withheld).
  const mine = row.wrappedKeys.find((w) => w.member === opts.member);
  if (!mine) {
    throw new Error(
      "handoff withheld: no wrapped key for this member on that artifact. It may be addressed to someone else, or the server is withholding your key. Ask the author to re-share.",
    );
  }

  const directory = await transport.memberPublicKeys();
  const author = directory.find((r) => r.member === row.authorMember);
  if (!author) {
    throw new Error("cannot verify handoff: author's public key is not in the directory");
  }
  if (!validRosterEntry(author)) {
    throw new Error("cannot verify handoff: author's directory entry is not validly self-signed (F-1)");
  }

  // Verified-room hard block: if the author's identity was pinned by a prior
  // `aethereum verify` and the directory now presents a DIFFERENT key, a
  // substituted key could otherwise sign a forged artifact that verifies.
  if (opts.pins && opts.pins.length > 0) {
    const stored = opts.pins.map((p) => ({ member: p.member, identityPub: unb64(p.identityPub) }));
    const presented = [{ member: author.member, identityPub: unb64(author.identityPub) }];
    const diverged = verify.detectPinDivergence(stored, presented).filter((d) => d.changed);
    if (diverged.length > 0) {
      throw new Error(
        `refusing to decrypt: the identity key for ${row.authorMember} changed since you verified this room. ` +
          "The server may be substituting keys. Re-run `aethereum verify` and compare the safety number out of band first.",
      );
    }
  }
  const authorIdentityPub = unb64(author.identityPub);

  // F-7/F-A6: verify the author signature over the row's own (keyEpoch,
  // sha256(ct)) under the FIXED handoff ref/version (see header) before trusting
  // anything enough to build the AAD.
  const ciphertext = unb64(row.ciphertext);
  const ctHash = e2ee.ciphertextHash(ciphertext);
  const authorVerified = e2ee.verifyArtifact(
    authorIdentityPub,
    {
      roomId: opts.roomId,
      ref: HANDOFF_REF,
      version: HANDOFF_VERSION,
      keyEpoch: row.keyEpoch,
      ciphertextHash: ctHash,
      contract: "",
    },
    unb64(row.authorSig),
  );
  if (!authorVerified) {
    throw new Error(
      "handoff author signature failed to verify (tampered ciphertext, relabelled metadata, or wrong author). Refusing to decrypt.",
    );
  }

  const wrapped: e2ee.WrappedKey = {
    member: mine.member,
    epk: unb64(mine.epk),
    nonce: unb64(mine.nonce),
    ct: unb64(mine.ct),
    keyEpoch: mine.keyEpoch,
    sig: unb64(mine.sig),
  };
  const contentKey = e2ee.unwrapRoomKey(wrapped, wrapPriv, authorIdentityPub);

  const plaintext = e2ee.decryptArtifact(contentKey, ciphertext, unb64(row.nonce), {
    roomId: opts.roomId,
    ref: HANDOFF_REF,
    version: HANDOFF_VERSION,
    keyEpoch: row.keyEpoch,
    alg: e2ee.E2EE_ALG,
  });

  return {
    payload: unpackHandoffPayload(plaintext),
    authorMember: row.authorMember,
    authorVerified: true,
  };
}
